import { useState, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

type UseAxiosReturn<T> = {
  data: T | null;
  isLoading: boolean;
  error: AxiosError | null;
  responses: { [key: string]: T | null };
  loadingStates: { [key: string]: boolean };
  errors: { [key: string]: AxiosError | null };
  sendRequest: (
    method: AxiosRequestConfig['method'],
    url: string,
    payload?: any,
    key?: string
  ) => Promise<void>;
};

const useAxios = <T = any>(): UseAxiosReturn<T> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const [responses, setResponses] = useState<{ [key: string]: T | null }>({});
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: string]: AxiosError | null }>({});

  const sendRequest = useCallback(
    async (method: AxiosRequestConfig['method'], url: string, payload: any = null, key = 'default') => {
      if (key === 'default') {
        setIsLoading(true);
        setError(null);
      } else {
        setLoadingStates((prev) => ({ ...prev, [key]: true }));
        setErrors((prev) => ({ ...prev, [key]: null }));
      }

      try {
        const response: AxiosResponse<T> = await axiosInstance({
          method,
          url,
          data: payload,
        });

        if (key === 'default') {
          setData(response.data);
        } else {
          setResponses((prev) => ({ ...prev, [key]: response.data }));
        }
      } catch (err) {
        const axiosError = err as AxiosError;
        if (key === 'default') {
          setError(axiosError);
        } else {
          setErrors((prev) => ({ ...prev, [key]: axiosError }));
        }
      } finally {
        if (key === 'default') {
          setIsLoading(false);
        } else {
          setLoadingStates((prev) => ({ ...prev, [key]: false }));
        }
      }
    },
    []
  );

  return { data, isLoading, error, responses, loadingStates, errors, sendRequest };
};

export default useAxios;
